import { useCallback, useEffect, useState } from 'react'
import { useParams, Link as RouterLink } from 'react-router-dom'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { useTranslation } from 'react-i18next'
import { Box, Typography, Alert, CircularProgress, Breadcrumbs, Button, Card, CardContent, Link as MuiLink } from '@mui/material'
import { NavigateNext as NavigateNextIcon, Home as HomeIcon } from '@mui/icons-material'
import { userSettingsApi, type StudentSettingsRestrictionReason } from '@/api/userSettings'
import { lessonSchedulesApi } from '@/api/lessonSchedules'
import { useUserStore } from '@/stores/userStore'
import { SettingsForm } from '@/components/settings/SettingsForm'
import { ScheduleReadonlyPreview } from '@/components/lessonSchedule/ScheduleReadonlyPreview'
import { UserLessonScheduleEditModal } from '@/components/lessonSchedule/UserLessonScheduleEditModal'
import { ROUTES } from '@/utils/constants'
import type { LessonScheduleTemplate, UserSettings } from '@/types'
import '@/styles/dashboard.css'

export function UserSettingsPage() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const userId = id ? parseInt(id, 10) : NaN
  const { currentUser, fetchUser, error, clearError } = useUserStore()
  const [settings, setSettings] = useState<UserSettings | null>(null)
  const [restrictionReason, setRestrictionReason] = useState<StudentSettingsRestrictionReason | null>(null)
  const [settingsLoading, setSettingsLoading] = useState(true)
  const [submitLoading, setSubmitLoading] = useState(false)
  const [settingsError, setSettingsError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const [schedulePreview, setSchedulePreview] = useState<LessonScheduleTemplate | null>(null)
  const [scheduleLoading, setScheduleLoading] = useState(false)
  const [scheduleError, setScheduleError] = useState<string | null>(null)
  const [scheduleEditOpen, setScheduleEditOpen] = useState(false)

  const groupId = currentUser?.contract?.group_id ?? null
  const isInGroup = Boolean(groupId)

  useEffect(() => {
    if (!id || Number.isNaN(userId)) return
    fetchUser(userId).catch(() => {})
  }, [id, userId, fetchUser])

  useEffect(() => {
    if (!userId || Number.isNaN(userId)) return
    setSettingsLoading(true)
    setSettingsError(null)
    userSettingsApi
      .getUserSettings(userId)
      .then((result) => {
        setSettings(result.settings)
        setRestrictionReason(result.restrictionReason)
      })
      .catch((err: Error) =>
        setSettingsError(err.message || t('profile.settings.loadError'))
      )
      .finally(() => setSettingsLoading(false))
  }, [userId, t])

  const loadSchedulePreview = useCallback(async () => {
    if (!userId || Number.isNaN(userId) || !currentUser) return
    setScheduleLoading(true)
    setScheduleError(null)
    try {
      if (groupId) {
        const s = await lessonSchedulesApi.getGroupSchedule(groupId)
        setSchedulePreview(s)
      } else {
        const s = await lessonSchedulesApi.getUserSchedule(userId)
        setSchedulePreview(s)
      }
    } catch (e: unknown) {
      const err = e as { message?: string }
      setScheduleError(err.message || t('lessonSchedules.loadError', 'Ошибка загрузки расписания'))
      setSchedulePreview(null)
    } finally {
      setScheduleLoading(false)
    }
  }, [userId, currentUser, groupId, t])

  useEffect(() => {
    void loadSchedulePreview()
  }, [loadSchedulePreview])

  const handleSubmit = async (
    data: Parameters<typeof userSettingsApi.updateUserSettings>[1]
  ) => {
    if (Number.isNaN(userId)) return
    setSettingsError(null)
    setSuccessMsg(null)
    setSubmitLoading(true)
    try {
      const updated = await userSettingsApi.updateUserSettings(userId, data)
      setSettings(updated)
      setSuccessMsg(t('profile.settings.saveSuccess'))
    } catch (err) {
      const message = err instanceof Error ? err.message : t('profile.settings.saveError')
      setSettingsError(message)
    } finally {
      setSubmitLoading(false)
    }
  }

  const studentDisplayName =
    currentUser?.surname || currentUser?.first_name
      ? [currentUser.surname, currentUser.first_name].filter(Boolean).join(' ')
      : currentUser?.email ?? t('profile.title')

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{ mb: 2 }}
        >
          <MuiLink component={RouterLink} to={ROUTES.DASHBOARD} underline="hover">
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            {t('dashboard.home')}
          </MuiLink>
          <MuiLink component={RouterLink} to={ROUTES.USERS} underline="hover">
            {t('dashboard.students')}
          </MuiLink>
          <MuiLink component={RouterLink} to={`/users/${userId}`} underline="hover">
            {studentDisplayName}
          </MuiLink>
          <Typography color="text.primary">{t('users.settings')}</Typography>
        </Breadcrumbs>

        <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
          {t('users.settings')}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          {t('users.settingsPageSubtitle')}
        </Typography>

        {settingsError && (
          <Alert severity="error" onClose={() => setSettingsError(null)} sx={{ mb: 2 }}>
            {settingsError}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={clearError} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {successMsg && (
          <Alert severity="success" onClose={() => setSuccessMsg(null)} sx={{ mb: 2 }}>
            {successMsg}
          </Alert>
        )}

        {currentUser && (
          <>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                mb: 2,
              }}
            >
              <Typography variant="h6" component="h2">
                {t('users.settingsSectionSchedule')}
              </Typography>
              {!isInGroup && (
                <Button
                  variant="contained"
                  onClick={() => setScheduleEditOpen(true)}
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  {t('users.settingsScheduleEdit', 'Редактировать расписание')}
                </Button>
              )}
            </Box>

            <Card
              variant="outlined"
              sx={{
                mb: 4,
                ...(isInGroup ? { bgcolor: 'var(--color-disable-surface)' } : {}),
              }}
            >
              <CardContent>
                {isInGroup && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t(
                        'users.settingsScheduleGroupHint',
                        'Ученик в группе: расписание задаётся группой, редактирование только на странице группы.'
                      )}
                    </Typography>
                    <MuiLink
                      component={RouterLink}
                      to={`/groups/${groupId}`}
                      underline="hover"
                      fontWeight={600}
                    >
                      {currentUser.contract?.group?.name
                        ? t('users.settingsScheduleOpenGroup', 'Группа: {{name}}', {
                            name: currentUser.contract.group.name,
                          })
                        : t('users.settingsScheduleOpenGroupShort', 'Перейти к группе')}
                    </MuiLink>
                  </Box>
                )}

                {scheduleError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setScheduleError(null)}>
                    {scheduleError}
                  </Alert>
                )}

                <ScheduleReadonlyPreview schedule={schedulePreview} loading={scheduleLoading} />
              </CardContent>
            </Card>
          </>
        )}

        {(settingsLoading || (!settings && !settingsError)) && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!settingsLoading && settings && (
          <>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
              {t('users.settingsSectionTests')}
            </Typography>
            <SettingsForm
              initialData={settings}
              onSubmit={handleSubmit}
              isLoading={submitLoading}
              studentSettingsRestriction={restrictionReason}
            />
          </>
        )}

        {!isInGroup && Number.isFinite(userId) && (
          <UserLessonScheduleEditModal
            open={scheduleEditOpen}
            onClose={() => setScheduleEditOpen(false)}
            userId={userId}
            schedule={schedulePreview}
            onSaved={loadSchedulePreview}
          />
        )}
      </Box>
    </DashboardLayout>
  )
}
